import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, TrendingUp, BarChart3, Globe, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { API } from "../../constants";

const TrafficDashboard = () => {
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [locationStats, setLocationStats] = useState(null);
  const [period, setPeriod] = useState("week");
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStartDate, setDeleteStartDate] = useState("");
  const [deleteEndDate, setDeleteEndDate] = useState("");
  const [deleting, setDeleting] = useState(false);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, summaryRes, locationRes] = await Promise.all([
        axios.get(`${API}/traffic/stats?period=${period}`),
        axios.get(`${API}/traffic/summary`),
        axios.get(`${API}/traffic/locations?period=${period}`)
      ]);
      setStats(statsRes.data);
      setSummary(summaryRes.data);
      setLocationStats(locationRes.data);
    } catch (error) {
      console.error("Error fetching traffic data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [period]);
  
  const handleDeleteData = async () => {
    if (!deleteStartDate || !deleteEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/traffic/data`, {
        data: {
          start_date: new Date(deleteStartDate).toISOString(),
          end_date: new Date(deleteEndDate).toISOString()
        }
      });
      toast.success(`Deleted ${res.data.deleted_count} records`);
      setShowDeleteModal(false);
      setDeleteStartDate("");
      setDeleteEndDate("");
      fetchData();
    } catch (error) {
      console.error("Error deleting data:", error);
      toast.error("Failed to delete data");
    } finally {
      setDeleting(false);
    }
  };
  
  const formatDuration = (seconds) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };
  
  const convertToUTC6 = (utcHour) => {
    const bdHour = (parseInt(utcHour) + 6) % 24;
    return bdHour;
  };
  
  const formatHour12 = (hour24) => {
    const h = parseInt(hour24);
    if (h === 0) return "12 AM";
    if (h === 12) return "12 PM";
    if (h < 12) return `${h} AM`;
    return `${h - 12} PM`;
  };
  
  const visitsByDateData = stats?.visits_by_date 
    ? Object.entries(stats.visits_by_date).map(([date, count]) => ({
        date: date.slice(5),
        visits: count
      }))
    : [];
  
  const pageViewsData = stats?.page_views
    ? Object.entries(stats.page_views)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([path, count]) => ({
          name: path === "/" ? "Home" : path.replace("/", "").replace("-", " "),
          value: count
        }))
    : [];
  
  const totalPageViews = pageViewsData.reduce((sum, item) => sum + item.value, 0);
  
  const visitsByHourData = stats?.visits_by_hour
    ? Object.entries(stats.visits_by_hour)
        .map(([hour, count]) => ({
          hour: convertToUTC6(hour),
          hourLabel: formatHour12(convertToUTC6(hour)),
          visits: count
        }))
        .sort((a, b) => a.hour - b.hour)
    : [];
  
  const peakHour = visitsByHourData.length > 0 
    ? visitsByHourData.reduce((max, item) => item.visits > max.visits ? item : max, visitsByHourData[0])
    : null;
  
  const DONUT_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6', '#f43f5e', '#6366f1'];
  
  // Location data for pie charts
  const cityData = locationStats?.cities || [];
  const regionData = locationStats?.regions || [];
  const countryData = locationStats?.countries || [];
  
  if (loading && !stats) {
    return (
      <div className="traffic-panel flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }
  
  return (
    <div className="traffic-panel">
      <div className="traffic-panel-inner">
        <div className="traffic-header">
          <div>
            <h2 className="traffic-title">Traffic Analytics</h2>
            <p className="traffic-subtitle">Monitor your website visitors and engagement (UTC+6)</p>
          </div>
          <div className="traffic-filters">
            <select 
              value={period} 
              onChange={(e) => setPeriod(e.target.value)}
              className="traffic-select"
            >
              <option value="day">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="6months">6 Months</option>
              <option value="all">All Time</option>
            </select>
            <button onClick={() => setShowDeleteModal(true)} className="traffic-btn traffic-btn-outline">
              <Trash2 className="h-4 w-4" />
              Clean Data
            </button>
            <button onClick={fetchData} className="traffic-btn traffic-btn-primary">
              <TrendingUp className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>
        
        <div className="traffic-kpi-grid">
          <div className="traffic-kpi traffic-kpi-red">
            <div className="traffic-kpi-label">Today</div>
            <div className="traffic-kpi-value">{summary?.today || 0}</div>
            <div className="traffic-kpi-sub">visits</div>
          </div>
          <div className="traffic-kpi traffic-kpi-orange">
            <div className="traffic-kpi-label">This Week</div>
            <div className="traffic-kpi-value">{summary?.this_week || 0}</div>
            <div className="traffic-kpi-sub">visits</div>
          </div>
          <div className="traffic-kpi traffic-kpi-blue">
            <div className="traffic-kpi-label">This Month</div>
            <div className="traffic-kpi-value">{summary?.this_month || 0}</div>
            <div className="traffic-kpi-sub">visits</div>
          </div>
          <div className="traffic-kpi traffic-kpi-green">
            <div className="traffic-kpi-label">Avg. Stay</div>
            <div className="traffic-kpi-value">{formatDuration(stats?.avg_duration_seconds || 0)}</div>
            <div className="traffic-kpi-sub">per visit</div>
          </div>
        </div>
        
        <div className="traffic-charts-grid">
          <div className="traffic-card">
            <div className="traffic-card-head">
              <div className="traffic-card-title">
                <TrendingUp className="h-4 w-4 text-orange-500" />
                Visits Over Time
              </div>
            </div>
            <div className="traffic-card-body">
              {visitsByDateData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={visitsByDateData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} />
                    <Tooltip 
                      contentStyle={{ 
                        borderRadius: '12px', 
                        border: 'none', 
                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                        background: 'rgba(255,255,255,0.95)'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="visits" 
                      stroke="#f97316" 
                      strokeWidth={3}
                      dot={{ fill: '#f97316', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: '#ea580c' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="traffic-no-data">
                  <BarChart3 className="h-10 w-10 opacity-30" />
                  <p>No data available</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="traffic-card">
            <div className="traffic-card-head">
              <div className="traffic-card-title">
                <Globe className="h-4 w-4 text-blue-500" />
                Top Pages
              </div>
            </div>
            <div className="traffic-card-body">
              {pageViewsData.length > 0 ? (
                <div className="traffic-donut-wrap">
                  <div className="traffic-donut-chart">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={pageViewsData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={85}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {pageViewsData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            borderRadius: '12px', 
                            border: 'none', 
                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="traffic-legend">
                    {pageViewsData.map((item, index) => (
                      <div key={index} className="traffic-legend-item">
                        <span className="flex items-center gap-2">
                          <span 
                            className="traffic-dot" 
                            style={{ backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length] }}
                          ></span>
                          <span className="capitalize truncate">{item.name}</span>
                        </span>
                        <b>{item.value}</b>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="traffic-no-data">
                  <Globe className="h-10 w-10 opacity-30" />
                  <p>No page data</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="traffic-card mt-4">
          <div className="traffic-card-head">
            <div className="traffic-card-title">
              <Clock className="h-4 w-4 text-purple-500" />
              Visits by Hour (UTC+6)
              {peakHour && (
                <span className="traffic-peak-badge">
                  🔥 Peak: {peakHour.hourLabel} ({peakHour.visits} visits)
                </span>
              )}
            </div>
          </div>
          <div className="traffic-card-body">
            {visitsByHourData.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={visitsByHourData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="hourLabel" stroke="#94a3b8" fontSize={10} interval={0} angle={-45} textAnchor="end" height={50} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '12px', 
                      border: 'none', 
                      boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
                    }}
                    formatter={(value) => [value, 'Visits']}
                  />
                  <Bar dataKey="visits" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="traffic-no-data">
                <Clock className="h-10 w-10 opacity-30" />
                <p>No hourly data</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="traffic-card mt-4">
          <div className="traffic-card-head">
            <div className="traffic-card-title">
              <BarChart3 className="h-4 w-4 text-green-500" />
              Period Summary
            </div>
          </div>
          <div className="traffic-card-body">
            <div className="traffic-summary-grid">
              <div className="traffic-mini">
                <div className="traffic-mini-value">{stats?.total_visits || 0}</div>
                <div className="traffic-mini-label">Total Visits</div>
              </div>
              <div className="traffic-mini">
                <div className="traffic-mini-value">{stats?.unique_visitors || 0}</div>
                <div className="traffic-mini-label">Unique Visitors</div>
              </div>
              <div className="traffic-mini">
                <div className="traffic-mini-value">{formatDuration(stats?.total_duration_seconds || 0)}</div>
                <div className="traffic-mini-label">Total Time</div>
              </div>
              <div className="traffic-mini">
                <div className="traffic-mini-value">{summary?.total_records || 0}</div>
                <div className="traffic-mini-label">Total Records</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Visitor Locations Section */}
        {(cityData.length > 0 || regionData.length > 0) && (
          <div className="traffic-section">
            <h3 className="traffic-section-title">📍 Visitor Locations</h3>
            <p className="text-gray-400 text-sm mb-6">Where your visitors are coming from</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Cities Pie Chart */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h4 className="text-gray-800 font-semibold mb-4">🏙️ By City/Area</h4>
                {cityData.length > 0 ? (
                  <>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={cityData}
                            cx="50%"
                            cy="50%"
                            innerRadius={45}
                            outerRadius={80}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {cityData.map((entry, index) => (
                              <Cell key={`cell-city-${index}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                            itemStyle={{ color: '#374151' }}
                            formatter={(value, name) => [`${value} visits`, name]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Legend below chart */}
                    <div className="mt-4 space-y-2">
                      {cityData.slice(0, 6).map((city, idx) => (
                        <div key={city.name} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: DONUT_COLORS[idx % DONUT_COLORS.length] }}></div>
                            <span className="text-sm text-gray-700">{city.name}</span>
                          </div>
                          <span className="text-sm font-semibold" style={{ color: DONUT_COLORS[idx % DONUT_COLORS.length] }}>{city.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-[250px] flex items-center justify-center">
                    <p className="text-gray-400 text-sm">City data collecting...</p>
                  </div>
                )}
              </div>
              
              {/* Regions Pie Chart */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h4 className="text-gray-800 font-semibold mb-4">🗺️ By Division/Region</h4>
                {regionData.length > 0 ? (
                  <>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={regionData}
                            cx="50%"
                            cy="50%"
                            innerRadius={45}
                            outerRadius={80}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {regionData.map((entry, index) => (
                              <Cell key={`cell-region-${index}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                            itemStyle={{ color: '#374151' }}
                            formatter={(value, name) => [`${value} visits`, name]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Legend below chart */}
                    <div className="mt-4 space-y-2">
                      {regionData.slice(0, 6).map((region, idx) => (
                        <div key={region.name} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: DONUT_COLORS[idx % DONUT_COLORS.length] }}></div>
                            <span className="text-sm text-gray-700">{region.name}</span>
                          </div>
                          <span className="text-sm font-semibold" style={{ color: DONUT_COLORS[idx % DONUT_COLORS.length] }}>{region.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-[250px] flex items-center justify-center">
                    <p className="text-gray-400 text-sm">Region data collecting...</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Location Stats Summary */}
            <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-blue-400" />
                  <span className="text-white font-medium">Location Data Available</span>
                </div>
                <span className="text-gray-400">{locationStats?.total_with_location || 0} visits with location</span>
              </div>
            </div>
          </div>
        )}
        
        {/* No Location Data Message */}
        {cityData.length === 0 && regionData.length === 0 && (
          <div className="traffic-section">
            <div className="text-center py-8">
              <Globe className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h4 className="text-white font-medium mb-2">No Location Data Yet</h4>
              <p className="text-gray-400 text-sm max-w-md mx-auto">
                Enable Location Tracking in Settings to start collecting visitor location data. 
                Visitors will be asked for permission to share their location.
              </p>
            </div>
          </div>
        )}
      </div>
      
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Clean Traffic Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Delete traffic data within a specific date range. This action cannot be undone.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={deleteStartDate}
                  onChange={(e) => setDeleteStartDate(e.target.value)}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={deleteEndDate}
                  onChange={(e) => setDeleteEndDate(e.target.value)}
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteData} 
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrafficDashboard;
